import { SIGNED_IN_LANDING_PATH } from "@/lib/auth-utils"

export type MarketingEntryCtaState = "loading" | "setup" | "get-started"

export type MarketingEntryCtaLabel = "setup" | "get-started"

export interface MarketingEntryCtaInput {
  /** Redux session has left "loading". A public page with no session stays here. */
  sessionReady: boolean
  /**
   * SSR already knows this is a registered user. Used before redux hydrates so
   * an anonymous or cookieless visit does not wait on a session that never arrives.
   */
  initialIsRegistered: boolean
  isAnonymous: boolean
  userProfileLoaded: boolean
  hasSetUpTravelAssistant: boolean
  hasShallowMemory: boolean
}

export interface MarketingEntryCtaDecision {
  state: MarketingEntryCtaState
  href?: typeof SIGNED_IN_LANDING_PATH
  /** Which label to keep in the layout while `state` is loading. */
  reserve?: MarketingEntryCtaLabel
}

export function resolveMarketingEntryCta(
  input: MarketingEntryCtaInput
): MarketingEntryCtaDecision {
  const {
    sessionReady,
    initialIsRegistered,
    isAnonymous,
    userProfileLoaded,
    hasSetUpTravelAssistant,
    hasShallowMemory,
  } = input

  // No session yet, and the page did not render a registered user: this is the
  // anonymous / unknown visit.
  if (!sessionReady && !initialIsRegistered) {
    return { state: "setup" }
  }

  if (!sessionReady || (!isAnonymous && !userProfileLoaded)) {
    return { state: "loading", reserve: "setup" }
  }

  const needsSetup =
    isAnonymous || (!hasSetUpTravelAssistant && hasShallowMemory)

  if (needsSetup) {
    return { state: "setup" }
  }

  return {
    state: "get-started",
    href: SIGNED_IN_LANDING_PATH,
  }
}
